export const createCsvContent = (
    rows: (string | number | null | undefined)[][],
    delimiter = ";"
): string => {
    return rows
        .map((row) =>
            row
                .map((value) => {
                    const stringValue = value ?? "";
                    const stringified =
                        typeof stringValue === "number" ? String(stringValue) : String(stringValue);
                    if (
                        stringified.includes(delimiter) ||
                        stringified.includes("\n") ||
                        stringified.includes('"')
                    ) {
                        return `"${stringified.replace(/"/g, '""')}"`;
                    }
                    return stringified;
                })
                .join(delimiter)
        )
        .join("\n");
};

export const toSafeFilename = (value: string, fallback = "export"): string => {
    const trimmed = value?.trim();
    if (!trimmed) return fallback;
    return trimmed.replace(/[^a-zA-Z0-9-_]/g, "_");
};

export interface ParsedCsvContent {
    headers: string[];
    rows: string[][];
    delimiter: string;
}

const detectDelimiter = (headerLine: string): string => {
    const commaMatches = headerLine.match(/,/g)?.length ?? 0;
    const semicolonMatches = headerLine.match(/;/g)?.length ?? 0;

    if (semicolonMatches > commaMatches) {
        return ";";
    }

    if (commaMatches > 0) {
        return ",";
    }

    return ",";
};

export const parseCsvContent = (
    content: string,
    options?: { delimiter?: string }
): ParsedCsvContent => {
    const trimmed = content?.trim();
    if (!trimmed) {
        throw new Error("Le fichier CSV est vide");
    }

    const firstLine = trimmed.split(/\r?\n/, 1)[0] ?? "";
    const delimiter = options?.delimiter ?? detectDelimiter(firstLine);

    const rows: string[][] = [];
    let currentRow: string[] = [];
    let currentField = "";
    let insideQuotes = false;

    const pushField = () => {
        currentRow.push(currentField);
        currentField = "";
    };

    const pushRow = () => {
        // Ignore empty trailing rows
        if (currentRow.length === 0 || currentRow.every((value) => value === "")) {
            currentRow = [];
            return;
        }
        rows.push(currentRow);
        currentRow = [];
    };

    for (let i = 0; i < content.length; i += 1) {
        const char = content[i];

        if (char === '"') {
            if (insideQuotes && content[i + 1] === '"') {
                currentField += '"';
                i += 1;
            } else {
                insideQuotes = !insideQuotes;
            }
            continue;
        }

        if (char === delimiter && !insideQuotes) {
            pushField();
            continue;
        }

        if ((char === "\n" || char === "\r") && !insideQuotes) {
            // Handle CRLF by skipping the next char if needed
            if (char === "\r" && content[i + 1] === "\n") {
                i += 1;
            }
            pushField();
            pushRow();
            continue;
        }

        currentField += char;
    }

    // Push the last field/row when file does not end with newline
    pushField();
    if (currentRow.length > 0) {
        pushRow();
    }

    if (rows.length === 0) {
        throw new Error("Le fichier CSV ne contient aucune donnée");
    }

    const [headerRow, ...dataRows] = rows;
    const headers = headerRow.map((header) => header.trim());
    const normalizedDataRows = dataRows.map((row) => {
        const normalizedRow = [...row];
        if (normalizedRow.length < headers.length) {
            while (normalizedRow.length < headers.length) {
                normalizedRow.push("");
            }
        }
        return normalizedRow;
    });

    return {
        headers,
        rows: normalizedDataRows,
        delimiter,
    };
};


